import { useCallback, useContext, useEffect, useState } from 'react';
import { useIcapsLogout } from './icaps/useIcapsLogout';
import { postUserReplacement } from '../network/endpoints/cart/postUserReplacement';
import { IdentityContext } from '../providers/IdentityProvider';
import { AuthEvent } from './icaps/useIcaps';

export const Actions = {
  LOG_OUT_BUSINESS_CUSTOMER: 'user/logoutBusinessCustomer',
  LOG_OUT_BUSINESS_CUSTOMER_SUCCESS: 'user/logoutBusinessCustomerSuccess',
  LOG_OUT_BUSINESS_CUSTOMER_FAIL: 'user/logoutBusinessCustomerFail',
} as const;

export type Actions = (typeof Actions)[keyof typeof Actions];

interface MoveBusinessCartItemsPayload {
  userId: string;
  provider: 'IRW' | 'Ciam';
}

function onLogoutFail({ status }: AuthEvent) {
  if (status === 'log-out-attempt-failure') {
    globalThis.ikea.pubsub.publish(Actions.LOG_OUT_BUSINESS_CUSTOMER_FAIL);
  }
}

export const useLogoutBusinessCustomer = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const { getSession } = useContext(IdentityContext);
  const icapsLogout = useIcapsLogout(onLogoutFail);

  const pubSubLoaded = useCallback(() => {
    if (globalThis?.ikea?.pubsub) {
      setIsLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (document.readyState === 'complete') {
      pubSubLoaded();
    } else {
      globalThis.addEventListener('load', pubSubLoaded);
      return () => {
        globalThis.removeEventListener('load', pubSubLoaded);
      };
    }
  }, [pubSubLoaded]);

  const moveBusinessCartItemsCallback = useCallback(
    async (payload: MoveBusinessCartItemsPayload) => {
      try {
        await getSession(true);
        await postUserReplacement(payload.userId, payload.provider);
        globalThis.ikea.pubsub.publish(Actions.LOG_OUT_BUSINESS_CUSTOMER_SUCCESS);
      } catch {
        globalThis.ikea.pubsub.publish(Actions.LOG_OUT_BUSINESS_CUSTOMER_FAIL);
      }
    },
    [getSession],
  );

  const logoutBusinessCustomerCallback = useCallback(
    (payload: MoveBusinessCartItemsPayload) => {
      try {
        icapsLogout(() => moveBusinessCartItemsCallback(payload));
      } catch {
        globalThis.ikea.pubsub.publish(Actions.LOG_OUT_BUSINESS_CUSTOMER_FAIL);
      }
    },
    [icapsLogout, moveBusinessCartItemsCallback],
  );

  useEffect(() => {
    if (isLoaded) {
      globalThis.ikea.pubsub.subscribe(Actions.LOG_OUT_BUSINESS_CUSTOMER, logoutBusinessCustomerCallback);
    }
    return () => {
      globalThis.ikea?.pubsub?.unsubscribe(Actions.LOG_OUT_BUSINESS_CUSTOMER, logoutBusinessCustomerCallback);
    };
  }, [isLoaded, logoutBusinessCustomerCallback]);
};
